import React, { memo, useMemo, useCallback } from 'react';
import Loading from '../Loading/Loading';
import './Table.css';

const Table = memo(({ 
  data = [], 
  columns = [],
  loading = false,
  title = "Tabel",
  emptyMessage = "Ingen data fundet",
  emptyDescription = "Der er ingen data at vise.",
  className = '',
  onRowClick,
  sortable = true,
  defaultSort = null,
  filters = null,
  actions = null,
  searchable = true,
  searchPlaceholder = "Søg...",
  searchFields = null,
  responsiveMode = 'scroll',
  mobileCardRenderer = null
}) => {
  const [sortConfig, setSortConfig] = React.useState(defaultSort);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [isMobile, setIsMobile] = React.useState(() => (
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false
  ));

  React.useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const mediaQuery = window.matchMedia('(max-width: 768px)');
    const handleChange = (event) => {
      setIsMobile(event.matches);
    };

    setIsMobile(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleChange);

    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const handleSort = useCallback((columnKey) => {
    if (!sortable) return;
    
    setSortConfig(prevConfig => {
      if (prevConfig?.key === columnKey) {
        return prevConfig.direction === 'asc' 
          ? { key: columnKey, direction: 'desc' }
          : { key: columnKey, direction: 'asc' };
      }
      return { key: columnKey, direction: 'asc' };
    });
  }, [sortable]);

  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  // Memoized filtered data based on search
  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) return data;
    
    const searchLower = searchTerm.toLowerCase();
    
    return data.filter(item => {
      // If searchFields is specified, only search in those fields
      if (searchFields && Array.isArray(searchFields)) {
        return searchFields.some(field => {
          const value = item[field];
          return value && value.toString().toLowerCase().includes(searchLower);
        });
      }
      
      // Otherwise search in all string/number values
      return Object.values(item).some(value => {
        if (value === null || value === undefined) return false;
        return value.toString().toLowerCase().includes(searchLower);
      });
    });
  }, [data, searchTerm, searchFields]);

  // Memoized sorted data
  const sortedData = useMemo(() => {
    if (!sortConfig) return filteredData;
    
    return [...filteredData].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      
      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [filteredData, sortConfig]);

  const handleRowClick = useCallback((rowData) => {
    if (onRowClick) {
      onRowClick(rowData);
    }
  }, [onRowClick]);

  const renderCellContent = useCallback((column, rowData, rowIndex) => {
    if (column.render) {
      return column.render(rowData, rowIndex);
    }
    
    if (column.key) {
      return rowData[column.key];
    }
    
    return '';
  }, []);

  if (loading) {
    return (
      <div className={`generic-table ${className}`}>
        <Loading text="Henter data..." />
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className={`generic-table ${className}`}>
        <div className="table-empty">
          <h3>{emptyMessage}</h3>
          <p>{emptyDescription}</p>
        </div>
      </div>
    );
  }

  if (filteredData.length === 0 && searchTerm.trim()) {
    return (
      <div className={`generic-table ${className}`}>
        <div className="table-header">
          <h2>{title}</h2>
          <div className="table-filters">
            {searchable && (
              <input
                type="text"
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={handleSearchChange}
                className="table-search-input"
              />
            )}
            {filters}
          </div>
        </div>
        <div className="table-empty">
          <h3>Ingen resultater fundet</h3>
          <p>Prøv at ændre din søgning eller ryd søgefeltet.</p>
        </div>
      </div>
    );
  }

  const shouldRenderCards = isMobile && responsiveMode === 'cards' && typeof mobileCardRenderer === 'function';

  return (
    <div className={`generic-table ${className}`}>
      <div className="table-header">
        <h2>{title}</h2>
        <div className="table-filters">
          {searchable && (
            <input
              type="text"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={handleSearchChange}
              className="table-search-input"
            />
          )}
          {filters}
        </div>
      </div>
      
      {shouldRenderCards ? (
        <div className="table-cards-container">
          {sortedData.map((rowData, rowIndex) => (
            <div
              key={rowData.id || rowIndex}
              className={`table-card ${onRowClick ? 'clickable' : ''}`}
              onClick={() => handleRowClick(rowData)}
              role={onRowClick ? 'button' : undefined}
              tabIndex={onRowClick ? 0 : undefined}
              onKeyDown={(event) => {
                if (!onRowClick) return;
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  handleRowClick(rowData);
                }
              }}
            >
              {mobileCardRenderer(rowData, rowIndex)}
            </div>
          ))}
        </div>
      ) : (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                {columns.map((column) => (
                  <th
                    key={column.key || column.title}
                    className={`table-header-cell ${sortable && column.sortable !== false ? 'sortable' : ''}`}
                    onClick={() => sortable && column.sortable !== false && handleSort(column.key)}
                    style={{ minWidth: column.minWidth }}
                  >
                    <div className="header-content">
                      <span>{column.title}</span>
                      {sortable && column.sortable !== false && sortConfig?.key === column.key && (
                        <span className="sort-indicator">
                          {sortConfig.direction === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
                {actions && <th className="table-header-cell actions-header">Handlinger</th>}
              </tr>
            </thead>
            <tbody>
              {sortedData.map((rowData, rowIndex) => (
                <tr
                  key={rowData.id || rowIndex}
                  className={`table-row ${onRowClick ? 'clickable' : ''}`}
                  onClick={() => handleRowClick(rowData)}
                >
                  {columns.map((column) => (
                    <td
                      key={column.key || column.title}
                      className="table-cell"
                      style={{
                        minWidth: column.minWidth,
                        maxWidth: column.minWidth,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {renderCellContent(column, rowData, rowIndex)}
                    </td>
                  ))}
                  {actions && (
                    <td
                      className="table-cell actions-cell"
                      style={{
                        minWidth: '200px',
                        maxWidth: '200px',
                        overflow: 'visible',
                        textOverflow: 'initial',
                        whiteSpace: 'normal'
                      }}
                    >
                      <div className="table-actions">
                        {actions(rowData, rowIndex)}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
});

Table.displayName = 'Table';

export default Table;